import React from 'react';

import {
  ContentGroup,
  Main,
  Heading,
  Paragraph,
  List,
  ListItem,
  Accordion,
  Link,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { StyledLink } from 'client/components/styledLink/StyledLink';
import Helmet from 'react-helmet';

import { MakingRepairsContent } from './MakingRepairsPage.config';
import * as routes from '../manifest';

function MakingRepairsPage() {
  const {
    metaContent,
    backLabel,
    title,
    yourResponsibility,
    asTheLandlord,
    enteringPropertyAccordion,
    scotlandAccordion,
    northernIrelandAccordion,
  } = useContent<MakingRepairsContent>();
  return (
    <Main>
      <Helmet>
        <title>{metaContent.title}</title>
      </Helmet>
      <ContentGroup marginTop="05" marginBottom="none">
        <StyledLink
          iconPosition="left"
          data-testid="back-button-link"
          to={routes.LandlordSupport}
        >
          {backLabel}
        </StyledLink>
      </ContentGroup>
      <Heading as="h1" size="s7" marginTop="05">
        {title}
      </Heading>
      <Paragraph>{yourResponsibility}</Paragraph>

      <Heading as="h2">{asTheLandlord.title}</Heading>
      <List>
        {asTheLandlord.listItems.map((item) => (
          <ListItem key={item.split(' ')[0]}>{item}</ListItem>
        ))}
      </List>
      <Paragraph>{asTheLandlord.blockOfFlats}</Paragraph>

      <Accordion
        data-testid="entering-property-accordion"
        label={enteringPropertyAccordion.label}
        variation="standalone"
      >
        <Paragraph>
          {enteringPropertyAccordion.enterPropertyForInspection}
        </Paragraph>

        <Heading as="h2" size="s2">
          {enteringPropertyAccordion.repairRefusalTitle}
        </Heading>
        <Paragraph>
          {enteringPropertyAccordion.tenantsCanClaim}
          <Link
            data-testid="hhsrs-link"
            href={enteringPropertyAccordion.hhsrsLinkHref}
            external
          >
            {enteringPropertyAccordion.hhsrsLinkLabel}
          </Link>
          {enteringPropertyAccordion.seriousHazards}
        </Paragraph>
        <Paragraph>{enteringPropertyAccordion.tenantsRepair}</Paragraph>

        <Heading as="h2" size="s2">
          {enteringPropertyAccordion.majorRepairsTitle}
        </Heading>
        <Paragraph>{enteringPropertyAccordion.askTenantsToMove}</Paragraph>
        <Heading as="h3" size="s2">
          {enteringPropertyAccordion.youMust}
        </Heading>
        <List>
          {enteringPropertyAccordion.majorRepairsListItems.map((item) => (
            <ListItem key={item.split(' ')[0]}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{enteringPropertyAccordion.cantRepossess}</Paragraph>
        <Paragraph>{enteringPropertyAccordion.reduceRent}</Paragraph>
        <Paragraph>
          {enteringPropertyAccordion.changeStandards}
          <Link
            data-testid="gov-link"
            href={enteringPropertyAccordion.govLinkHref}
            external
          >
            {enteringPropertyAccordion.govLinkLabel}
          </Link>
          {enteringPropertyAccordion.beforeStart}
        </Paragraph>
        <Paragraph marginBottom="none">
          {enteringPropertyAccordion.standardsInWales}
          <Link href={enteringPropertyAccordion.welshGovLinkHref} external>
            {enteringPropertyAccordion.welshGovLinkLabel}
          </Link>
          {enteringPropertyAccordion.period}
        </Paragraph>
      </Accordion>

      <Accordion
        data-testid="scotland-accordion"
        label={scotlandAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph>{scotlandAccordion.recognisedStandards}</Paragraph>

        <Heading as="h2" size="s2">
          {scotlandAccordion.repairingStandardTitle}
        </Heading>
        <Paragraph>{scotlandAccordion.basicLevel}</Paragraph>

        <Heading as="h3" size="s2">
          {scotlandAccordion.meetRepairStandardTitle}
        </Heading>
        <List>
          {scotlandAccordion.meetRepairStandardListItems.map((item) => (
            <ListItem key={`${item.split(' ')[2]}-`}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{scotlandAccordion.commonAreas}</Paragraph>
        <Paragraph>{scotlandAccordion.acquireConsent}</Paragraph>
        <Paragraph>
          <Link
            data-testid="repairing-standard-guidance-link"
            href={scotlandAccordion.repairingGuidanceLinkHref}
            external
          >
            {scotlandAccordion.repairingGuidanceLinkLabel}
          </Link>
        </Paragraph>
        <Paragraph>{scotlandAccordion.gasSafety}</Paragraph>
        <Heading as="h3" size="s2">
          {scotlandAccordion.doesntMeetStandardsTitle}
        </Heading>
        <Paragraph>
          {scotlandAccordion.tenantReport}
          <Link
            data-testid="repairing-standard-property-chamber-link"
            href={scotlandAccordion.housingChamberLinkHref}
            external
          >
            {scotlandAccordion.housingChamberLinkLabel}
          </Link>
          {scotlandAccordion.period}
        </Paragraph>

        <Heading as="h2" size="s2">
          {scotlandAccordion.tolerableStandardsTitle}
        </Heading>
        <Paragraph>{scotlandAccordion.tolerableBasicLevel}</Paragraph>
        <Heading as="h3" size="s2">
          {scotlandAccordion.meetTolerableStandardTitle}
        </Heading>
        <List>
          {scotlandAccordion.meetTolerableStandardListItems.map((item) => (
            <ListItem key={item}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{scotlandAccordion.cookerSpace}</Paragraph>
        <Paragraph>
          <Link
            data-testid="tolerable-standard-guidance-link"
            href={scotlandAccordion.tolerableStandardLinkHref}
            external
          >
            {scotlandAccordion.tolerableStandardLinkLabel}
          </Link>
        </Paragraph>
        <Heading as="h3" size="s2">
          {scotlandAccordion.doesntMeetStandardsTitle}
        </Heading>
        <Paragraph marginBottom="none">
          {scotlandAccordion.reportToHousingChamber}
          <Link
            data-testid="tolerable-standard-property-chamber-link"
            href={scotlandAccordion.propertyChamberLinkHref}
            external
          >
            {scotlandAccordion.propertyChamberLinkLabel}
          </Link>
          {scotlandAccordion.period}
        </Paragraph>
      </Accordion>

      <Accordion
        data-testid="northern-ireland-accordion"
        label={northernIrelandAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph>{northernIrelandAccordion.asALandlord}</Paragraph>
        <Heading as="h3" size="s2">
          {northernIrelandAccordion.responsibleTitle}
        </Heading>
        <List>
          {northernIrelandAccordion.responsibleListItems.map((item) => (
            <ListItem key={item}>{item}</ListItem>
          ))}
        </List>
        <Paragraph marginBottom="none">
          <Link
            data-testid="northern-ireland-responsibilities-link"
            href={northernIrelandAccordion.responsibilitiesLinkHref}
            external
          >
            {northernIrelandAccordion.responsibilitiesLinkLabel}
          </Link>
        </Paragraph>
      </Accordion>
    </Main>
  );
}

export default MakingRepairsPage;
